import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import InfoBulle from "@/components/ui/info-bulle";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import {
  hasBlockingWarning,
  ImportAnalysisReport,
  ImportTable,
} from "@/lib/import/import-types";
import { ImportBlockersBanner } from "../components/ImportBlockersBanner";
import { ImportColumnsReview } from "../components/ImportColumnsReview";
import { ImportDataHighlights } from "../components/ImportDataHighlights";

/**
 * Etape 4 : le diagnostic, avant tout arbitrage et toute ecriture.
 *
 * L'analyse ne modifie rien : l'ecran le dit explicitement plutot que de laisser
 * croire a un import realise. Les etapes suivantes confirment les liaisons, puis
 * demandent une validation avant d'ecrire.
 */
export const ImportStepReview = ({
  report,
  jobId,
  table,
  pendingCount,
  onReportChange,
  onContinue,
  onBack,
}: {
  report: ImportAnalysisReport;
  jobId: string | null;
  table: ImportTable;
  /** correspondances de liaison restant a confirmer */
  pendingCount: number;
  /** le rapport a ete recalcule (correspondance de colonnes enregistree) */
  onReportChange: (report: ImportAnalysisReport) => void;
  onContinue: () => void;
  onBack: () => void;
}) => {
  const vt = useVisibleT();
  const [acknowledged, setAcknowledged] = useState(false);

  const blocked = hasBlockingWarning(report);
  const advisoryCount = report.warnings.filter((warning) => !warning.blocking).length;
  const needsAcknowledgement = advisoryCount > 0 && !acknowledged;
  // Une colonne non tranchée n'est jamais ignorée en silence : on ne continue
  // pas tant que chaque colonne n'a pas été rattachée ou ignorée explicitement.
  const columnsUndecided = !report.mappingResolved;

  return (
    <div className="flex flex-col gap-6">
      <ImportBlockersBanner
        report={report}
        acknowledged={acknowledged}
        onAcknowledgedChange={setAcknowledged}
      />

      <ImportDataHighlights report={report} />

      <Separator />

      <ImportColumnsReview
        report={report}
        jobId={jobId}
        table={table}
        onReportChange={onReportChange}
      />

      {/*
        Section « À savoir avant d'importer » masquée à la demande du client :
        c'est un doublon des rappels (dont « une cellule vide ne supprime rien »)
        déjà affichés à l'étape de Confirmation. Décommenter pour la rétablir.

        <Separator />

        <section className="flex flex-col gap-3">
          <h3 className="text-base font-semibold">
            {vt("bo.import.review.beforeImport", "À savoir avant d'importer")}
          </h3>
          <ul className="flex flex-col gap-1.5 text-sm text-gray-700 list-disc pl-4">
            {report.disclaimers.map((disclaimer) => (
              <li key={disclaimer}>{disclaimer}</li>
            ))}
          </ul>
        </section>
      */}

      {!blocked && (
        <InfoBulle
          message={
            pendingCount > 0
              ? `${pendingCount} ${vt(
                  "bo.import.review.pendingLinks",
                  "correspondance(s) de liaison restent à confirmer à l'étape suivante. Rien n'est encore écrit dans votre base.",
                )}`
              : vt(
                  "bo.import.review.readyToWrite",
                  "Votre fichier est prêt. Rien n'est encore écrit dans votre base : vous validerez à l'étape suivante.",
                )
          }
        />
      )}

      <div className="flex flex-wrap justify-between gap-3">
        <Button variant="ghost" onClick={onBack}>
          {vt("bo.import.review.anotherFile", "Analyser un autre fichier")}
        </Button>
        <Button
          disabled={blocked || needsAcknowledgement || columnsUndecided}
          title={
            blocked
              ? vt(
                  "bo.import.review.blockedFirst",
                  "Corrigez les points bloquants ci-dessus.",
                )
              : columnsUndecided
                ? vt(
                    "bo.import.review.decideColumnsFirst",
                    "Tranchez d'abord les colonnes non reconnues ci-dessus.",
                  )
                : needsAcknowledgement
                  ? vt(
                      "bo.import.review.acknowledgeFirst",
                      "Prenez connaissance des points à vérifier ci-dessus.",
                    )
                  : undefined
          }
          onClick={onContinue}
        >
          {vt("bo.import.continue", "Continuer")}
        </Button>
      </div>
    </div>
  );
};
